import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { selectUser } from "redux/authReducer";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from "react-router-dom";

import {
  PRIVATE_DATASETS_PATH,
  PUBLIC_DATASETS_PATH,
} from "helpers/constants/paths";

import Layout from "views/layout";
import Login from "views/pages/Login";
import DatasetInventory from "views/pages/DatasetInventory";
import DataPage from "views/pages/DataPage";
import MobileScreenErrorModal from "views/modals/MobileScreenErrorModal";
import DatasetCreationPage from "views/pages/DatasetCreationPage";
import ScanResultsPage from "views/pages/ScanResultsPage";

import "./App.scss";
import { fetchUserConfig, fetchUserInfo } from "redux/User/actions";
import { isFastdupUser, isOnPremUser } from "helpers/utility/utilities";
import ErrorComponent from "views/layout/ErrorComponent";
import TaskManagerPage from "views/pages/TaskManager";
import LoginLayout from "views/components/LoginLayout";
import BrowserSupportModal from "views/modals/BrowserSupportModal";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConfigProvider } from "./views/providers/ConfigProvider";
import ModelCatalogPage from "views/pages/ModelCatalog";
import WorkspaceSettingsPage from "views/pages/WorkspaceSettings";
import { DatasetPageWrapper } from "./views/pages/common/DatasetPageWrapper";

const LoginRouter = () => {
  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route element={<LoginLayout />}>
        <Route
          path="/login"
          element={<Login />}
          errorElement={<ErrorComponent />}
        />
      </Route>,
    ),
  );
  return <RouterProvider router={router} />;
};

const DataPageRouter = () => {
  return createBrowserRouter(
    createRoutesFromElements(
      <Route path="/" element={<Layout />} errorElement={<ErrorComponent />}>
        <Route
          path="/dataset/:datasetId/data"
          element={<DataPage />}
          errorElement={<ErrorComponent />}
        />
        <Route
          path="/dataset/:datasetId/data/cluster/:clusterId"
          element={<DataPage />}
          errorElement={<ErrorComponent />}
        />
        <Route
          path="/dataset/:datasetId/data/image/:imageId"
          element={<DataPage />}
          errorElement={<ErrorComponent />}
        />
        <Route
          path="/dataset/:datasetId/data/cluster/:clusterId/image/:imageId"
          element={<DataPage />}
          errorElement={<ErrorComponent />}
        />
      </Route>,
    ),
  );
};
const DataAndLegacyRouter = () => {
  return createBrowserRouter(
    createRoutesFromElements(
      <Route path="/" element={<Layout />} errorElement={<ErrorComponent />}>
        <Route
          path="/"
          element={<DatasetInventory />}
          errorElement={<ErrorComponent />}
        />
        <Route
          path={PRIVATE_DATASETS_PATH}
          element={<DatasetInventory />}
          errorElement={<ErrorComponent />}
        />
        <Route
          path={PUBLIC_DATASETS_PATH}
          element={<DatasetInventory />}
          errorElement={<ErrorComponent />}
        />
        <Route path="/dataset" element={<DatasetPageWrapper />}>
          <Route path="/dataset/:datasetId/data" element={<DataPage />} />
          <Route
            path="/dataset/:datasetId/data/seed/:seedId"
            element={<DataPage />}
          />
          <Route
            path="/dataset/:datasetId/data/cluster/:clusterId"
            element={<DataPage />}
          />
          <Route
            path="/dataset/:datasetId/data/image/:imageId"
            element={<DataPage />}
          />
          <Route
            path="/dataset/:datasetId/data/cluster/:clusterId/image/:imageId"
            element={<DataPage />}
          />
          <Route
            path="/dataset/:newDatasetId/create"
            element={<DatasetCreationPage />}
          />
          <Route
            path="/dataset/:newDatasetId/update"
            element={<DatasetCreationPage />}
          />
          <Route
            path="/dataset/:newDatasetId/update/:batchId"
            element={<DatasetCreationPage />}
          />
          <Route
            path="/dataset/:newDatasetId/create/on-prem"
            element={<DatasetCreationPage />}
          />
          <Route
            path="/dataset/:newDatasetId/update/on-prem"
            element={<DatasetCreationPage />}
          />
          <Route
            path="/dataset/:newDatasetId/update/:batchId/on-prem"
            element={<DatasetCreationPage />}
          />
          {/* No :newDatasetId - the dataset is created on Continue, after the user has
              chosen which scan results to import, so cancelling leaves no orphan row. */}
          <Route path="/dataset/scan-results" element={<ScanResultsPage />} />
          {/* Same page, adding the exported folders to an existing dataset. */}
          <Route
            path="/dataset/:datasetId/scan-results"
            element={<ScanResultsPage />}
          />
        </Route>
        <Route
          path="/task-manager"
          element={<TaskManagerPage />}
          errorElement={<ErrorComponent />}
        />
        <Route
          path="/models-catalog"
          element={<ModelCatalogPage />}
          errorElement={<ErrorComponent />}
        />
        <Route
          path="/workspace-settings"
          element={<WorkspaceSettingsPage />}
          errorElement={<ErrorComponent />}
        />
      </Route>,
    ),
  );
};
const WrappedRoutes = () => {
  return (
    <RouterProvider
      router={isFastdupUser() ? DataPageRouter() : DataAndLegacyRouter()}
    />
  );
};

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false } },
});

const RouterApp = () => {
  const dispatch = useDispatch();
  const pathname = window.location.pathname.split("/")[1];
  const isLoginRoute = ["login"].includes(pathname);
  const [user, setUser] = useState(selectUser());
  const requiresLogin =
    process.env.REACT_APP_IS_AUTHENTICATION_DISABELED !== "true" &&
    isOnPremUser() &&
    !isLoginRoute;
  // With OIDC login, keep the app hidden until the session is confirmed so
  // unauthenticated users go straight to the login page.
  const [authChecked, setAuthChecked] = useState(!requiresLogin);

  useEffect(() => {
    if (requiresLogin && !user) {
      window.location.href = "/api/v1/oidc/login";
      return;
    }
    (async () => {
      await dispatch(fetchUserConfig() as any);
      const currentUser = selectUser();
      // A 401/403 logs the user out (clears SESSION and redirects to login).
      if (requiresLogin && !currentUser) {
        return;
      }
      if (!user) {
        setUser(currentUser);
      }
      setAuthChecked(true);
    })();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (user) {
      dispatch(fetchUserInfo(user) as any);
    }
  }, [user, dispatch]);

  if (!authChecked) {
    return null;
  }

  return (
    <ConfigProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserSupportModal />
        <MobileScreenErrorModal />
        {isLoginRoute ? <LoginRouter /> : <WrappedRoutes />}
      </QueryClientProvider>
    </ConfigProvider>
  );
};

export default RouterApp;
